import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Button, Select, App } from 'antd';
import { IgnitionCoilItem } from '../../types/ProductItems';
import RefOemManager from '../common/RefOemManager';
import ImageUploader from '../common/ImageUploader';
import { toBase64, mapBase64ToUploadList } from '../../utils/formsThumbnails';
import { fetchProductRefsOems } from '../../services/productAPI';

interface IgnitionCoilFormProps {
  onAdd: (item: IgnitionCoilItem) => void;
  initialValues?: IgnitionCoilItem | null;
}

const { Option } = Select;

const nonNegativeRule = (label: string) => ({
  validator(_: any, value: any) {
    if (value === undefined || value === '' || Number(value) >= 0) return Promise.resolve();
    return Promise.reject(new Error(`${label} не может быть отрицательным`));
  },
});

const IgnitionCoilForm: React.FC<IgnitionCoilFormProps> = ({ onAdd, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [refList, setRefList] = useState<{ [brand: string]: string[] }>({});
  const [oemList, setOemList] = useState<{ [model: string]: string[] }>({});

  const resetForm = useCallback(() => {
    form.resetFields();
    setRefList({});
    setOemList({});
  }, [form]);

  useEffect(() => {
    let mounted = true;
    if (initialValues) {
      const { images = [], ...rest } = initialValues;
      form.setFieldsValue({
        ...rest,
        images: mapBase64ToUploadList(images),
      });
      setRefList(initialValues.references ?? {});
      setOemList(initialValues.oems ?? {});
      const mysqlId = Number(initialValues.id);
      if (Number.isInteger(mysqlId) && mysqlId > 0) {
        fetchProductRefsOems('ignition_coils', mysqlId).then(fresh => {
          if (fresh && mounted) {
            setRefList(fresh.references);
            setOemList(fresh.oems);
          }
        });
      }
    } else {
      resetForm();
    }
    return () => { mounted = false; };
  }, [initialValues, form, resetForm]);

  const handleFinish = useCallback(
    async (values: any) => {
      try {
        const imagesField = form.getFieldValue('images') || [];
        const base64Images = await Promise.all(
          imagesField.map(async (file: any) =>
            file?.originFileObj ? await toBase64(file.originFileObj) : file?.url || file?.thumbUrl
          )
        );

        const originalServerUrls = (initialValues?.images ?? [])
          .filter((img: string) => typeof img === 'string' && !img.startsWith('data:'));
        const _deletedImgs = originalServerUrls.filter((img: string) => !base64Images.includes(img));

        const newItem: IgnitionCoilItem = {
          id: initialValues?.id || crypto.randomUUID(),
          key: initialValues?.key || Date.now().toString(),
          ...values,
          images: base64Images,
          references: refList,
          oems: oemList,
          _deletedImgs,
        } as any;

        await onAdd(newItem);
        message.success('Товар добавлен или обновлён!');
        resetForm();
      } catch {
        message.error('Заполните все обязательные поля');
      }
    },
    [form, refList, oemList, onAdd, initialValues, resetForm]
  );

  return (
    <Form
      layout="vertical"
      form={form}
      name="IgnitionCoilForm"
      preserve={false}
      onFinish={handleFinish}
    >
      <Form.Item label="Бренд" name="brand" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="Номер детали" name="partNumber" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="SKU" name="sku">
        <Input maxLength={10} />
      </Form.Item>

      <Form.Item label="Модель" name="model" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item label="Цена" name="price" rules={[{ required: true }, nonNegativeRule('Цена')]}>
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Количество (в наличии)"
        name="quantity"
        rules={[{ required: true }, nonNegativeRule('Количество')]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Кол-во штекеров"
        name="connectorCount"
        rules={[{ required: true }, nonNegativeRule('Кол-во штекеров')]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label="Кол-во контактов"
        name="contactCount"
        rules={[{ required: true }, nonNegativeRule('Кол-во контактов')]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item label="Тип" name="original" rules={[{ required: true }]}>
        <Select placeholder="Выберите тип">
          <Option value="original">Оригинал</Option>
          <Option value="copy">Копия</Option>
        </Select>
      </Form.Item>

      {/* если аплоадер не мапит fileList сам — можно завернуть его в Form.Item name="images" */}
      <ImageUploader />

      <RefOemManager title="REF" data={refList} setData={setRefList} />
      <RefOemManager title="OEM" data={oemList} setData={setOemList} />

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={resetForm}>Очистить форму</Button>
        <Button type="primary" htmlType="submit">
          Добавить продукт
        </Button>
      </div>
    </Form>
  );
};

export default IgnitionCoilForm;
